import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Area,
  CartesianGrid,
  Line,
  ComposedChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { listBills } from "../../lib/api-modules/bills.js";
import { listIncomeEvents } from "../../lib/api-modules/income-events.js";
import { projectCashFlow } from "../../lib/util/cash-flow.js";
import { formatCentsCompact } from "../../lib/util/money.js";
import { Card, Pill } from "../design-system/index.js";

/**
 * 12-month cash-flow projection sparkline. Recharts ComposedChart with
 * a net-cents Area + cumulative-cents Line overlay so the spouse sees
 * both "are we positive each month" and "where does the running total
 * land" at a glance.
 *
 * **Live data, not stored.** The widget projects on every render from
 * the current bills + income tables. Rationale lives in
 * `web/src/lib/util/cash-flow.ts`. (Phase 6 will swap to scenario-aware
 * projection that overlays user-saved overrides; the widget shape
 * stays the same.)
 *
 * **Privacy mode** masks Y-axis ticks + tooltip + the headline net
 * pill to `••••`, matching the NetWorthChart convention.
 */

interface CashFlowSparklineProps {
  readonly hideValues: boolean;
  readonly horizonMonths?: number;
  readonly startingBalanceCents?: number;
}

export function CashFlowSparkline({
  hideValues,
  horizonMonths = 12,
  startingBalanceCents = 0,
}: CashFlowSparklineProps) {
  const billsQuery = useQuery({
    queryKey: ["bills", { includeArchived: false }],
    queryFn: () => listBills(false),
  });

  // Lookback 6 months for income — enough to smooth bonus + RSU vest
  // lumpiness while staying recent enough to reflect current comp.
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const incomeFrom = useMemo(() => {
    const d = new Date(today);
    d.setUTCMonth(d.getUTCMonth() - 6);
    return d.toISOString().slice(0, 10);
  }, [today]);

  const incomeQuery = useQuery({
    queryKey: ["income-events", { from: incomeFrom }],
    queryFn: () => listIncomeEvents({ from: incomeFrom }),
  });

  const projection = useMemo(() => {
    const bills = billsQuery.data?.bills ?? [];
    const income = incomeQuery.data?.income_events ?? [];
    return projectCashFlow({
      bills,
      income_events: income,
      horizon_months: horizonMonths,
      today,
      starting_balance_cents: startingBalanceCents,
    });
  }, [billsQuery.data, incomeQuery.data, horizonMonths, today, startingBalanceCents]);

  const isLoading = billsQuery.isLoading || incomeQuery.isLoading;
  const isError = billsQuery.isError || incomeQuery.isError;
  const hasData =
    (billsQuery.data?.bills.length ?? 0) > 0 ||
    (incomeQuery.data?.income_events.length ?? 0) > 0;

  const chartData = useMemo(
    () =>
      projection.months.map((m) => ({
        month: m.month,
        net_cents: m.net_cents,
        cumulative_cents: m.cumulative_cents,
      })),
    [projection.months],
  );

  return (
    <Card className="mb-6" padded>
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-muted)]">
            Cash flow — next {horizonMonths} months
          </p>
          <p className="mt-0.5 text-xs text-[var(--color-muted)]">
            Projected from your bills + recent income.
          </p>
        </div>
        {!isLoading && !isError && hasData && (
          <Pill tone={projection.net_cents >= 0 ? "success" : "danger"}>
            <span data-testid="cashflow-net">
              {projection.net_cents >= 0 ? "+" : ""}
              {formatCentsCompact(projection.net_cents, { hidden: hideValues })}{" "}
              net
            </span>
          </Pill>
        )}
      </div>

      {isLoading ? (
        <p
          className="py-8 text-center text-sm text-[var(--color-muted)]"
          data-testid="cashflow-loading"
        >
          Loading…
        </p>
      ) : isError ? (
        <p
          className="py-8 text-center text-sm text-[var(--color-danger)]"
          data-testid="cashflow-error"
        >
          Couldn't load cash-flow projection.
        </p>
      ) : !hasData ? (
        <div
          className="rounded-md border border-dashed border-[var(--color-border-default)] p-8 text-center text-sm text-[var(--color-muted)]"
          data-testid="cashflow-empty"
        >
          Add bills + income to see your cash-flow projection.
        </div>
      ) : (
        <div className="h-[200px] w-full" data-testid="cashflow-chart">
          <ResponsiveContainer width="100%" height="100%" minWidth={0}>
            <ComposedChart
              data={chartData}
              margin={{ top: 16, right: 16, bottom: 8, left: 0 }}
            >
              <defs>
                <linearGradient id="cashFlowFill" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="0%"
                    stopColor="var(--color-accent)"
                    stopOpacity={0.35}
                  />
                  <stop
                    offset="100%"
                    stopColor="var(--color-accent)"
                    stopOpacity={0.02}
                  />
                </linearGradient>
              </defs>
              <CartesianGrid strokeOpacity={0.1} vertical={false} />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 11, fill: "currentColor", fillOpacity: 0.7 }}
                tickFormatter={shortMonth}
                minTickGap={20}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "currentColor", fillOpacity: 0.7 }}
                tickFormatter={(v: number) =>
                  hideValues ? "••••" : formatCentsCompact(v)
                }
                width={64}
              />
              <Tooltip
                content={
                  hideValues
                    ? () => null
                    : ({ active, payload, label }) => {
                        if (!active || !payload || payload.length === 0) {
                          return null;
                        }
                        const net = payload.find(
                          (p) => p.dataKey === "net_cents",
                        )?.value as number | undefined;
                        const cum = payload.find(
                          (p) => p.dataKey === "cumulative_cents",
                        )?.value as number | undefined;
                        return (
                          <div className="rounded-md border border-[var(--color-border-default)] bg-[var(--color-card)] px-3 py-2 text-xs shadow-[var(--shadow-card)]">
                            <div className="font-medium text-[var(--color-text)]">
                              {shortMonth(String(label))}
                            </div>
                            {typeof net === "number" && (
                              <div className="font-mono tabular-nums text-[var(--color-accent)]">
                                Net: {net >= 0 ? "+" : ""}
                                {formatCentsCompact(net)}
                              </div>
                            )}
                            {typeof cum === "number" && (
                              <div className="font-mono tabular-nums text-[var(--color-muted)]">
                                Running: {formatCentsCompact(cum)}
                              </div>
                            )}
                          </div>
                        );
                      }
                }
              />
              <Area
                type="monotone"
                dataKey="net_cents"
                stroke="var(--color-accent)"
                strokeWidth={2}
                fill="url(#cashFlowFill)"
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="cumulative_cents"
                stroke="var(--color-accent)"
                strokeWidth={1.5}
                strokeOpacity={0.5}
                strokeDasharray="4 4"
                dot={false}
                isAnimationActive={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

function shortMonth(yyyymm: string): string {
  const [y, m] = yyyymm.split("-");
  if (!y || !m) return yyyymm;
  const month = MONTHS[Number(m) - 1] ?? m;
  return `${month} '${y.slice(2)}`;
}
